import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import i18n from "src/translationService";
import { CalendarOutlined } from "@ant-design/icons";
import { Alert, Form, InputNumber, message, Radio, Spin, type TableProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
  type SVGProps,
} from "react";
import { useTranslation } from "react-i18next";
import CloneIcon from "sipli-fleet-core/assets/icons/clone-icon.svg";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import ConfigurationSuccessIcon from "src/assets/svgs/configuration-success-icon.svg?url";
import PresetCreatedModalIcon from "src/assets/svgs/preset-created-modal-icon.svg?url";
import {
  LayersControl,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import type { VirtuosoHandle } from "react-virtuoso";
import {
  CustomActionButtons,
  CustomButton,
  CustomEmpty,
  CustomInput,
  CustomModal,
  CustomSelect,
  CustomSharedDatePicker,
  CustomTable,
  CustomTimeRangePicker,
  CustomTooltip,
  CustomTypography,
  CustomVirtualScroll,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import { useQueryGetDeviceMappings } from "src/queries/mapping.queries";
import {
  useMutationCloneRoutePreset,
  useMutationDeleteRoutePreset,
  useMutationExportRoutePreset,
  useMutationUseRoutePreset,
  useQueryGetRoutePresets,
  useQueryGetRoutePresetsPagedList,
} from "src/queries/routePresets.queries";
import { useQueryGetRouteHistory } from "src/queries/routeHistory.queries";
import {
  useMutationSaveRouteSetting,
  useQueryGetRouteSettingByVehicleId,
} from "src/queries/routeSettings.queries";
import type { RouteHistoryPoint } from "src/services/routeHistoryService";
import type { RoutePresetRecord, RoutePresetSortDirection } from "src/services/routePresetsService";
import type {
  DetectionBasedOn,
  RouteMode,
  RouteSettingPayload,
  RouteSettingRecord,
  StartStopCriteria,
} from "src/services/routeSettingsService";
import { getApiErrorMessage } from "src/util/apiError";
import { cn } from "src/util/cn";
import { useEpochFormatter } from "../../../../../sipli-fleet-core/components/EpochDate/useEpochFormatter";

interface RoutesHistoryTabProps {
  vehicleId?: string;
  unitId?: string;
}

interface NormalizedRoutePoint extends RouteHistoryPoint {
  id: string;
  latitude: number;
  longitude: number;
}

const DEFAULT_ROUTE_CENTER = {
  latitude: 40.9085,
  longitude: 14.336,
};

const { BaseLayer } = LayersControl;

const parseCoordinate = (value: RouteHistoryPoint["latitude"]) => {
  if (value === null || value === undefined || value === "") return null;

  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
};

const normalizeRoutePoints = (points: RouteHistoryPoint[] = []): NormalizedRoutePoint[] => {
  const seen = new Set<string>();

  return points.reduce<NormalizedRoutePoint[]>((records, point, index) => {
    const latitude = parseCoordinate(point.latitude);
    const longitude = parseCoordinate(point.longitude);

    if (latitude === null || longitude === null) return records;

    const key = `${point.timestamp ?? index}-${latitude}-${longitude}`;
    if (seen.has(key)) return records;

    seen.add(key);
    records.push({
      ...point,
      id: key,
      latitude,
      longitude,
    });

    return records;
  }, []);
};

const formatSpeed = (speed: RouteHistoryPoint["speed"]) => {
  if (speed === null || speed === undefined || speed === "") return "--";

  return `${formatNumber(speed, i18n.language)} km/h`;
};

const formatRouteTime = (timestamp?: string) => {
  if (!timestamp) return "--";

  const dateTime = dayjs(timestamp);
  return dateTime.isValid() ? dateTime.format("HH.mm") : timestamp;
};

const PopupTimeIcon = () => (
  <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 16 16" width="16">
    <g clipPath="url(#route-history-popup-time-icon)">
      <path
        d="M8 0C3.58769 0 0 3.58769 0 8C0 12.4123 3.58769 16 8 16C12.4123 16 16 12.4123 16 8C16 3.58769 12.4123 0 8 0ZM8 14.7692C4.26462 14.7692 1.23077 11.7354 1.23077 8C1.23077 4.26462 4.26462 1.23077 8 1.23077C11.7354 1.23077 14.7692 4.26462 14.7692 8C14.7692 11.7354 11.7354 14.7692 8 14.7692Z"
        fill="#666E8D"
      />
      <path d="M7.38473 7.38425H4.31396V8.61502H8.6155V4.31348H7.38473V7.38425Z" fill="#666E8D" />
    </g>
    <defs>
      <clipPath id="route-history-popup-time-icon">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
);

const PopupSpeedIcon = () => (
  <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 17 16" width="17">
    <g clipPath="url(#route-history-popup-speed-icon)">
      <path
        d="M8.7905 16C6.60899 16 4.55665 15.1748 3.00663 13.6783L4.01845 12.6853C5.29578 13.9231 6.98932 14.6014 8.7905 14.6014C12.522 14.6014 15.5647 11.6434 15.5647 8C15.5647 4.35664 12.522 1.3986 8.7905 1.3986C6.96062 1.3986 5.25272 2.0979 3.96821 3.36364L2.94922 2.38462C4.49924 0.846154 6.57311 0 8.7905 0C13.3186 0 16.9999 3.58741 16.9999 8C16.9999 12.4126 13.3186 16 8.7905 16Z"
        fill="#666E8D"
      />
      <path
        d="M10.2337 4.13055L8.1709 7.65332L9.41744 8.34648L11.4802 4.82371L10.2337 4.13055Z"
        fill="#666E8D"
      />
      <path d="M4.49937 4.34277H0V6.09103H4.49937V4.34277Z" fill="#666E8D" />
      <path d="M4.49937 9.90918H0V11.6574H4.49937V9.90918Z" fill="#666E8D" />
      <path d="M6.16423 7.12598H2.15283V8.87423H6.16423V7.12598Z" fill="#666E8D" />
      <path
        d="M8.79052 9.24439C9.49597 9.24439 10.0678 8.6871 10.0678 7.99964C10.0678 7.31218 9.49597 6.75488 8.79052 6.75488C8.08506 6.75488 7.51318 7.31218 7.51318 7.99964C7.51318 8.6871 8.08506 9.24439 8.79052 9.24439Z"
        fill="#666E8D"
      />
    </g>
    <defs>
      <clipPath id="route-history-popup-speed-icon">
        <rect fill="white" height="16" width="17" />
      </clipPath>
    </defs>
  </svg>
);

const PopupCalendarIcon = () => (
  <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 17 16" width="17">
    <g clipPath="url(#route-history-popup-calendar-icon)">
      <path
        d="M13.0769 3.2064V0H11.7692V3.2064H5.23731V0H3.92962V3.2064H0V16H17V3.2064H13.0769ZM15.6923 4.4864V7.0016H1.30769V4.4864H15.6923ZM1.30769 14.72V8.2816H15.6923V14.72H1.30769Z"
        fill="#666E8D"
      />
    </g>
    <defs>
      <clipPath id="route-history-popup-calendar-icon">
        <rect fill="white" height="16" width="17" />
      </clipPath>
    </defs>
  </svg>
);

const PopupLocationIcon = () => (
  <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 13 16" width="13">
    <g clipPath="url(#route-history-popup-location-icon)">
      <path
        d="M6.89304 15.822C6.68436 16.0552 6.31608 16.0552 6.1074 15.822L2.44923 11.7161C1.49172 10.6421 0.785866 9.45761 0.350078 8.19946C0.116839 7.53663 0.00635733 6.95972 0.00635733 6.49329C0.000219464 2.91523 2.91571 0 6.50022 0C10.0847 0 13.0002 2.91523 13.0002 6.49942C13.0002 6.96586 12.8836 7.53663 12.6565 8.2056C12.2207 9.46375 11.5149 10.6421 10.5573 11.7223L6.89918 15.8282L6.89304 15.822ZM6.50022 1.22746C3.59087 1.22746 1.22779 3.59033 1.22779 6.49942C1.22779 6.8247 1.326 7.27273 1.504 7.80054C1.88454 8.90526 2.51061 9.9486 3.36377 10.8999L6.50022 14.4166L9.63667 10.8937C10.4898 9.93633 11.1098 8.89298 11.4964 7.7944C11.6806 7.26659 11.7726 6.81857 11.7726 6.49329C11.7726 3.5842 9.40957 1.22133 6.50022 1.22133V1.22746ZM6.50022 9.18143C5.02099 9.18143 3.81797 7.97852 3.81797 6.49942C3.81797 5.02033 5.02099 3.81741 6.50022 3.81741C7.97945 3.81741 9.18247 5.02033 9.18247 6.49942C9.18247 7.97852 7.97945 9.18143 6.50022 9.18143ZM6.50022 5.04488C5.69616 5.04488 5.04555 5.69544 5.04555 6.49942C5.04555 7.30341 5.69616 7.95397 6.50022 7.95397C7.30428 7.95397 7.95489 7.30341 7.95489 6.49942C7.95489 5.69544 7.30428 5.04488 6.50022 5.04488Z"
        fill="#666E8D"
      />
    </g>
    <defs>
      <clipPath id="route-history-popup-location-icon">
        <rect fill="white" height="16" width="13" />
      </clipPath>
    </defs>
  </svg>
);

const ROUTE_PRIMARY_COLOR = "#3867D6";

const createRouteIcon = (active: boolean) => {
  const size = 24;
  const halfSize = size / 2;

  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background-color: ${active ? ROUTE_PRIMARY_COLOR : "#ffffff"};
        border: 3px solid ${ROUTE_PRIMARY_COLOR};
        box-sizing: border-box;
      "></div>
    `,
    className: "",
    iconAnchor: [halfSize, halfSize],
    iconSize: [size, size],
    popupAnchor: [0, -halfSize],
  });
};

const routeActiveIcon = createRouteIcon(true);
const routeInactiveIcon = createRouteIcon(false);

const getRouteIcon = (selected: boolean) => (selected ? routeActiveIcon : routeInactiveIcon);

const CenterRouteMap = ({ points }: { points: NormalizedRoutePoint[] }) => {
  const map = useMap();

  useEffect(() => {
    const resizeTimer = window.setTimeout(() => {
      map.invalidateSize();
    }, 100);

    if (!points.length) {
      map.setView([DEFAULT_ROUTE_CENTER.latitude, DEFAULT_ROUTE_CENTER.longitude], 13);
      return () => window.clearTimeout(resizeTimer);
    }

    const positions = points.map((point) => [point.latitude, point.longitude] as [number, number]);

    if (positions.length === 1) {
      const firstPosition = positions[0] as [number, number];
      map.setView(firstPosition, 13);
    } else {
      map.fitBounds(L.latLngBounds(positions), { padding: [80, 80] });
    }

    return () => window.clearTimeout(resizeTimer);
  }, [map, points]);

  return null;
};

const RouteMarker = ({
  point,
  selected,
  onSelectPoint,
  onClosePopup,
}: {
  point: NormalizedRoutePoint;
  selected: boolean;
  onSelectPoint: (point: NormalizedRoutePoint) => void;
  onClosePopup: (pointId: string) => void;
}) => {
  const markerRef = useRef<L.Marker | null>(null);
  const formatEpoch = useEpochFormatter();

  useEffect(() => {
    if (selected) {
      markerRef.current?.openPopup();
    } else {
      markerRef.current?.closePopup();
    }
  }, [selected]);

  return (
    <Marker
      ref={markerRef}
      position={[point.latitude, point.longitude]}
      icon={getRouteIcon(selected)}
      eventHandlers={{
        click: () => onSelectPoint(point),
        popupclose: () => onClosePopup(point.id),
      }}
    >
      <Popup className="route-history-leaflet-popup">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-[16px]">
            <span className="flex items-center gap-2 whitespace-nowrap font-bold text-[#1f2433]">
              <PopupTimeIcon />
              <span className="text-[14px] leading-[18px] font-[600]">
                {formatRouteTime(point.timestamp)}
              </span>
            </span>
            <span className="text-gray8">|</span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <PopupCalendarIcon />
              <span className="text-[14px] leading-[18px] text-gray9">
                {formatEpoch(point.timestamp)}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-[16px]">
            <span className="flex items-center gap-2 whitespace-nowrap font-bold text-[#1f2433]">
              <PopupSpeedIcon />
              <span className="text-[14px] leading-[18px] font-[600]">
                {formatSpeed(point.speed)}
              </span>
            </span>
            <span className="text-[#8b94b6]">|</span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <PopupLocationIcon />
              <span
                title={`${formatNumber(point.latitude, i18n.language, { minimumFractionDigits: 6, maximumFractionDigits: 6 })}, ${formatNumber(point.longitude, i18n.language, { minimumFractionDigits: 6, maximumFractionDigits: 6 })}`}
                className="text-[14px] leading-[18px] text-gray9"
              >
                {formatNumber(point.latitude, i18n.language, { minimumFractionDigits: 6, maximumFractionDigits: 6 })}, {formatNumber(point.longitude, i18n.language, { minimumFractionDigits: 6, maximumFractionDigits: 6 })}
              </span>
            </span>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

const RouteHistoryMap = ({
  points,
  selectedPoint,
  onSelectPoint,
  onClosePopup,
}: {
  points: NormalizedRoutePoint[];
  selectedPoint?: NormalizedRoutePoint;
  onSelectPoint: (point: NormalizedRoutePoint) => void;
  onClosePopup: (pointId: string) => void;
}) => {
  const { t } = useTranslation();
  const routePositions = useMemo(
    () => points.map((point) => [point.latitude, point.longitude] as [number, number]),
    [points]
  );

  return (
    <div className="relative h-[570px] min-h-[420px] overflow-hidden bg-[#eef2f7]">
      <MapContainer
        center={[DEFAULT_ROUTE_CENTER.latitude, DEFAULT_ROUTE_CENTER.longitude]}
        zoom={13}
        zoomControl={false}
        scrollWheelZoom
        wheelPxPerZoomLevel={120}
        wheelDebounceTime={80}
        zoomSnap={0.5}
        zoomDelta={0.5}
        doubleClickZoom={false}
        className="h-full w-full"
      >
        <LayersControl position="bottomleft">
          <BaseLayer name="Default Map">
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </BaseLayer>
          <BaseLayer checked name="Satellite View">
            <TileLayer url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" />
          </BaseLayer>
          <BaseLayer name="Terrain View">
            <TileLayer url="https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}" />
          </BaseLayer>
        </LayersControl>

        {points.map((point) => (
          <RouteMarker
            key={point.id}
            point={point}
            selected={selectedPoint?.id === point.id}
            onSelectPoint={onSelectPoint}
            onClosePopup={onClosePopup}
          />
        ))}

        {routePositions.length > 0 && (
          <Polyline positions={routePositions} color="#3867d6" weight={3} />
        )}

        <CenterRouteMap points={points} />
      </MapContainer>

      {!points.length && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            className="pointer-events-auto rounded-[8px] bg-white px-6 py-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <CustomEmpty description={t("noRoutesFound")} />
          </div>
        </div>
      )}
    </div>
  );
};

const routeSections = [
  { key: "history", labelKey: "routesHistory" },
  // { key: "settings", labelKey: "settings" },
  // { key: "configuration", labelKey: "configuration" },
  // { key: "preset", labelKey: "preset" },
  {
    key: "settings",
    labelKey: "settings",
    children: [
      { key: "configuration", labelKey: "configuration" },
      { key: "preset", labelKey: "preset" },
    ],
  },
];

const IGNITION_START_STOP_EVENTS_MAPPING = "ignition_start_stop_events";
const CloneIconComponent = CloneIcon as unknown as ComponentType<SVGProps<SVGSVGElement>>;
const DeleteIconComponent = DeleteIcon as unknown as ComponentType<SVGProps<SVGSVGElement>>;

type SelectOption<T extends string | number = string | number> = {
  label: string;
  value: T;
};

interface RouteSettingFormValues {
  routeMode: RouteMode;
  startStopCriteria: StartStopCriteria;
  detectionBasedOn: DetectionBasedOn;
  minimumSpeedToStartARoute?: number | null;
  geofenceInM?: number | null;
  validationPoint?: number | null;
  timeOut?: number | null;
  timeOutWithoutData?: number | null;
  stoppedTimeoutSec?: number | null;
  timeoutNoData?: number | null;
  maxSpeedKmh: number;
}

const routeModeOptions: SelectOption<RouteMode>[] = [
  { label: "Managed by ignition", value: "MANAGED_BY_IGNITION" },
  { label: "Managed by server", value: "MANAGED_BY_SERVER" },
];
const managedByServerLabel = "Managed by server";

const startStopCriteriaOptions: SelectOption<StartStopCriteria>[] = [
  { label: "Ignition", value: "IGNITION" },
  { label: "Only by criteria", value: "ONLY_CRITERIA" },
];

const detectionBasedOnOptions: SelectOption<DetectionBasedOn>[] = [
  { label: "Speed", value: "SPEED" },
  { label: "GPS coords", value: "GPS_COORDS" },
];

const maxSpeedOptions: SelectOption<number>[] = Array.from({ length: 30 }, (_, index) => {
  const speed = (index + 1) * 10;
  return { label: `${speed} km/h`, value: speed };
});

const getLatestRouteSetting = (
  response?: RouteSettingRecord | RouteSettingRecord[] | null
): RouteSettingRecord | null => {
  if (Array.isArray(response)) return response.slice().reverse().find(Boolean) ?? null;
  return response ?? null;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildRouteSettingFormValues = (
  routeSetting: RouteSettingRecord | null,
  hasIgnitionStartStopEvents: boolean
): RouteSettingFormValues => ({
  routeMode: routeSetting?.routeMode ?? "MANAGED_BY_SERVER",
  startStopCriteria: routeSetting?.startStopCriteria ?? "ONLY_CRITERIA",
  detectionBasedOn: hasIgnitionStartStopEvents
    ? (routeSetting?.detectionBasedOn ?? "SPEED")
    : "SPEED",
  minimumSpeedToStartARoute: toNumberOrNull(routeSetting?.minSpeedKmh),
  geofenceInM: toNumberOrNull(routeSetting?.geofenceMeters),
  validationPoint: toNumberOrNull(routeSetting?.validationPoints),
  timeOut: toNumberOrNull(routeSetting?.idleTimeoutSec),
  timeOutWithoutData: toNumberOrNull(routeSetting?.noDataTimeoutSec),
  stoppedTimeoutSec: toNumberOrNull(routeSetting?.stoppedTimeoutSec),
  timeoutNoData: routeSetting?.timeoutNoData ? Number(routeSetting.timeoutNoData) / 60 : 0,
  maxSpeedKmh: toNumberOrNull(routeSetting?.maxSpeedKmh) || 160,
});

const RouteSettingNumberInput = (props: ComponentProps<typeof InputNumber>) => (
  <InputNumber
    {...props}
    className={cn("!h-[32px] !w-full [&_.ant-input-number-input]:!h-[30px]", props.className)}
    min={props.min ?? 0}
    onWheel={(event) => {
      props.onWheel?.(event);
      event.currentTarget.blur();
    }}
  />
);

const RouteSettingSaveSuccessModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <CustomModal
      open={open}
      onCancel={onClose}
      showFooter={false}
      width={473}
      styles={{ content: { minHeight: 252 } }}
      centered
      destroyOnHidden
    >
      <div className="flex flex-col items-center text-center mt-2">
        <img
          src={ConfigurationSuccessIcon}
          alt=""
          aria-hidden="true"
          className="h-[50px] w-[49px]"
        />

        <CustomTypography.CustomText className="!mt-8 !mb-0 !text-[19.93px] !leading-[16px] !font-bold !text-black">
          {t("configuration_saved", { defaultValue: "Configuration saved" })}
        </CustomTypography.CustomText>

        <CustomTypography.CustomParagraph className="!mt-4 !mb-0 !max-w-full !text-[14px] !leading-[18px] !font-normal !text-gray8">
          {t("configuration_saved_successfully", {
            defaultValue: "The new configuration has been successfully saved.",
          })}
        </CustomTypography.CustomParagraph>

        <CustomButton
          type="primary"
          label={t("close")}
          onClick={onClose}
          className="!mt-10 !h-[32px] w-full !shrink-0 !text-[14px] !leading-[18px] !font-semibold !text-gray2"
        />
      </div>
    </CustomModal>
  );
};

const RoutePresetCreateSuccessModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <CustomModal
      open={open}
      onCancel={onClose}
      showFooter={false}
      width={473}
      styles={{ content: { minHeight: 270 } }}
      centered
      destroyOnHidden
    >
      <div className="flex flex-col items-center text-center mt-3">
        <img src={PresetCreatedModalIcon} alt="" aria-hidden="true" />

        <CustomTypography.CustomText className="!mt-8 !mb-0 !text-[19.93px] !leading-[16px] !font-bold !text-black">
          {t("preset_created_title", { defaultValue: "Preset created" })}
        </CustomTypography.CustomText>

        <CustomTypography.CustomParagraph className="!mt-3 !mb-0 !max-w-[400px] !text-[14px] !leading-[18px] !font-normal !text-gray8">
          {t("preset_created_description", {
            defaultValue:
              "The new preset has been successfully created, you can find the preset in Manage Presets.",
          })}
        </CustomTypography.CustomParagraph>

        <CustomButton
          type="primary"
          label={t("close")}
          onClick={onClose}
          className="!mt-7.5 !h-[32px] w-full !shrink-0 !text-[14px] !leading-[18px] !font-semibold !text-gray2"
        />
      </div>
    </CustomModal>
  );
};

const getRoutePresetNames = (data?: { data?: { response?: RoutePresetRecord[] } }) =>
  (data?.data?.response ?? [])
    .map((preset) => preset.name?.trim().toLowerCase())
    .filter((name): name is string => Boolean(name));

const RouteSettingExportPresetModal = ({
  open,
  routeSetting,
  onClose,
  onSuccess,
}: {
  open: boolean;
  routeSetting?: RouteSettingRecord | null;
  onClose: () => void;
  onSuccess: (messageText: string) => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ name: string }>();
  const presetNamesQuery = useQueryGetRoutePresets({ enabled: open });
  const exportMutation = useMutationExportRoutePreset();
  const nameValue = Form.useWatch("name", form) ?? "";
  const existingNames = useMemo(
    () => getRoutePresetNames(presetNamesQuery.data),
    [presetNamesQuery.data]
  );
  const isDuplicate =
    Boolean(nameValue.trim()) && existingNames.includes(nameValue.trim().toLowerCase());

  useEffect(() => {
    if (open) form.resetFields();
  }, [form, open]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const trimmedName = values.name.trim();

    if (existingNames.includes(trimmedName.toLowerCase())) {
      form.setFields([{ name: "name", errors: [t("preset_name_already_exists")] }]);
      return;
    }

    try {
      await exportMutation.mutateAsync({
        name: trimmedName,
        routeMode: routeSetting?.routeMode,
        startStopCriteria: routeSetting?.startStopCriteria,
        detectionBasedOn: routeSetting?.detectionBasedOn,
        minSpeedKmh: routeSetting?.minSpeedKmh,
        geofenceMeters: routeSetting?.geofenceMeters,
        validationPoints: routeSetting?.validationPoints,
        idleTimeoutSec: routeSetting?.idleTimeoutSec,
        noDataTimeoutSec: routeSetting?.noDataTimeoutSec,
      });
      form.resetFields();
      onClose();
      onSuccess(t("route_preset_exported_successfully"));
    } catch {
      form.setFields([{ name: "name", errors: [t("error")] }]);
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={onClose}
      showFooter={false}
      width={473}
      closable={false}
      styles={{ content: { minHeight: 230 } }}
      destroyOnHidden
      centered
    >
      <CustomTypography.CustomText className="!block !text-center !text-[19.93px] !leading-[28px] !font-bold !text-black">
        {t("new_preset", { defaultValue: "New preset" })}
      </CustomTypography.CustomText>

      <Form form={form} layout="vertical" requiredMark={false} className="!mt-7">
        <Form.Item
          name="name"
          label={t("preset_name", { defaultValue: "Preset name" })}
          className="
            !mb-0
            [&_.ant-form-item-label]:!pb-0
            [&_.ant-form-item-label]:!min-h-0
            [&_.ant-form-item-label>label]:!mb-0
            [&_.ant-form-item-label>label]:!text-[14px]
            [&_.ant-form-item-label>label]:!leading-[18px]
            [&_.ant-form-item-label>label]:!font-normal
            [&_.ant-form-item-label>label]:!text-gray9
          "
          validateStatus={isDuplicate ? "error" : undefined}
          help={isDuplicate ? t("preset_name_already_exists") : undefined}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("preset_name_is_required"),
            },
          ]}
        >
          <CustomInput
            placeholder={t("type_preset_name", {
              defaultValue: "Type preset name",
            })}
            className="common-input !mt-2 !h-[32px] !py-0"
          />
        </Form.Item>
      </Form>
      <CustomActionButtons
        okBtnLabel={t("save")}
        cancelBtnLabel={t("cancel")}
        loading={exportMutation.isPending}
        disabled={isDuplicate}
        onOk={handleSubmit}
        onCancel={onClose}
        className="mt-8 !h-[32px]"
        cancelProps={{
          className: "!h-full flex-1 !text-[14px] !leading-[18px] !font-semibold !text-primary",
        }}
        okProps={{
          className: "!h-full flex-1 !text-[14px] !leading-[18px] !font-semibold !text-gray2",
        }}
      />
    </CustomModal>
  );
};

const RouteSettingUsePresetModal = ({
  open,
  vehicleId,
  onClose,
  onSuccess,
  onError,
}: {
  open: boolean;
  vehicleId?: string;
  onClose: () => void;
  onSuccess: (messageText: string) => void | Promise<void>;
  onError: (messageText: string) => void;
}) => {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState<RoutePresetRecord | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const presetsQuery = useQueryGetRoutePresets({ enabled: open });
  const usePresetMutation = useMutationUseRoutePreset();
  const presets = presetsQuery.data?.data.response ?? [];

  useEffect(() => {
    if (open) {
      setSelectedPreset(null);
      setShowConfirm(false);
    }
  }, [open]);

  const handleUsePreset = async () => {
    if (!selectedPreset || !vehicleId) return;

    try {
      await usePresetMutation.mutateAsync({ id: selectedPreset.id, vehicleId });
      await presetsQuery.refetch();
      await onSuccess(t("use_preset_applied_successfully"));
      setShowConfirm(false);
      onClose();
    } catch {
      onError(t("error"));
    }
  };

  return (
    <>
      <CustomModal
        open={open}
        title={t("useAPresets", { defaultValue: "Use a Preset" })}
        onCancel={onClose}
        showFooter={false}
        width={600}
        zIndex={1200}
        destroyOnHidden
        centered
      >
        <Spin spinning={presetsQuery.isLoading || presetsQuery.isFetching}>
          {presets.length ? (
            <div className="max-h-[240px] overflow-y-auto rounded-[4px] border border-gray2">
              {presets.map((preset, index) => {
                const isSelected = selectedPreset?.id === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-gray1 cursor-pointer",
                      index !== presets.length - 1 && "border-b border-gray2"
                    )}
                    onClick={() => setSelectedPreset(isSelected ? null : preset)}
                  >
                    <Radio checked={isSelected} />
                    <CustomTypography.CustomText className="!text-sm !font-medium !text-black">
                      {preset.name || "--"}
                    </CustomTypography.CustomText>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-[170px] items-center justify-center">
              <CustomEmpty description={t("data_not_found")} />
            </div>
          )}
        </Spin>

        <CustomActionButtons
          okBtnLabel={t("use", { defaultValue: "Use" })}
          cancelBtnLabel={t("close")}
          loading={usePresetMutation.isPending}
          disabled={!selectedPreset || !vehicleId}
          onOk={() => setShowConfirm(true)}
          onCancel={onClose}
          className="mt-5"
        />
      </CustomModal>

      <CustomModal
        open={showConfirm}
        onCancel={() => setShowConfirm(false)}
        width={430}
        showFooter={false}
        destroyOnHidden
        centered
        zIndex={2000}
      >
        <div className="flex flex-col items-center pt-2 pb-0 text-center">
          <div className="mb-4 flex h-[33px] w-[33px] items-center justify-center rounded-full bg-[#FAAD14] text-[24px] font-bold leading-none text-white">
            !
          </div>

          <div className="text-[18px] font-semibold leading-[28px] text-gray10">
            {t("use_preset_confirmation", {
              defaultValue: "Are you sure you want to use this preset ?",
            })}
          </div>

          <CustomActionButtons
            cancelBtnLabel={t("no", { defaultValue: "No" })}
            loading={usePresetMutation.isPending}
            onCancel={() => setShowConfirm(false)}
            okBtnLabel={t("yes", { defaultValue: "Yes" })}
            onOk={handleUsePreset}
            className="mt-5"
          />
        </div>
      </CustomModal>
    </>
  );
};

const RouteSettingConfiguration = ({
  vehicleId,
  unitId,
}: {
  vehicleId?: string;
  unitId?: string;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RouteSettingFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [isExportPresetOpen, setIsExportPresetOpen] = useState(false);
  const [isUsePresetOpen, setIsUsePresetOpen] = useState(false);
  const [isSaveSuccessOpen, setIsSaveSuccessOpen] = useState(false);
  const [isPresetCreateSuccessOpen, setIsPresetCreateSuccessOpen] = useState(false);

  const routeSettingQuery = useQueryGetRouteSettingByVehicleId(vehicleId, {
    enabled: !!vehicleId,
  });
  const mappingsQuery = useQueryGetDeviceMappings(unitId, {
    enabled: !!unitId,
  });
  const saveMutation = useMutationSaveRouteSetting();

  const routeSetting = useMemo(
    () => getLatestRouteSetting(routeSettingQuery.data?.data.response),
    [routeSettingQuery.data?.data.response]
  );
  const hasIgnitionStartStopEvents = useMemo(
    () =>
      Boolean(
        mappingsQuery.data?.data.response?.linkedMappings?.some(
          (mapping) => mapping.mappingTypeId === IGNITION_START_STOP_EVENTS_MAPPING
        )
      ),
    [mappingsQuery.data?.data.response?.linkedMappings]
  );

  const routeMode = Form.useWatch("routeMode", form);
  const detectionBasedOn = Form.useWatch("detectionBasedOn", form);
  const showCriteriaFields = routeMode !== "MANAGED_BY_IGNITION";
  const routeModeLabel =
    routeModeOptions.find((option) => option.value === routeMode)?.label ?? managedByServerLabel;

  useEffect(() => {
    form.setFieldsValue(buildRouteSettingFormValues(routeSetting, hasIgnitionStartStopEvents));
  }, [form, hasIgnitionStartStopEvents, routeSetting]);

  const resetForm = () => {
    form.setFieldsValue(buildRouteSettingFormValues(routeSetting, hasIgnitionStartStopEvents));
  };

  const handleSubmit = async (values: RouteSettingFormValues) => {
    if (!vehicleId) return;

    const payload: RouteSettingPayload = {
      vehicleId,
      routeMode: hasIgnitionStartStopEvents ? values.routeMode : "MANAGED_BY_SERVER",
      startStopCriteria: hasIgnitionStartStopEvents ? values.startStopCriteria : "ONLY_CRITERIA",
      detectionBasedOn: values.detectionBasedOn,
      minSpeedKmh: values.detectionBasedOn === "SPEED" ? values.minimumSpeedToStartARoute : null,
      maxSpeedKmh: values.maxSpeedKmh,
      geofenceMeters: values.detectionBasedOn === "GPS_COORDS" ? values.geofenceInM : null,
      validationPoints: values.validationPoint,
      idleTimeoutSec: values.timeOut,
      noDataTimeoutSec: values.timeOutWithoutData,
      timeoutNoData: values.timeoutNoData ? Number(values.timeoutNoData) * 60 : 0,
      stoppedTimeoutSec: Number(values.stoppedTimeoutSec ?? 0),
      ...(routeSetting?.id ? { id: routeSetting.id } : {}),
    };

    try {
      await saveMutation.mutateAsync(payload);
      setIsSaveSuccessOpen(true);
    } catch (error) {
      messageApi.error(getApiErrorMessage(error) ?? t("route_setting_save_failed"));
    }
  };

  const isLoading =
    routeSettingQuery.isLoading ||
    routeSettingQuery.isFetching ||
    mappingsQuery.isLoading ||
    mappingsQuery.isFetching;
  const isSaving = saveMutation.isPending;

  return (
    <div className="p-4">
      {contextHolder}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <CustomTypography.CustomText className="!text-[17.72px] !leading-[28px] !font-bold !text-black">
          {t("configuration")}
        </CustomTypography.CustomText>

        <div className="flex flex-wrap items-center justify-end gap-2 h-[32px]">
          <CustomButton
            type="secondary"
            label={t("use_preset", { defaultValue: "Use preset" })}
            onClick={() => setIsUsePresetOpen(true)}
            disabled={!vehicleId}
            className="min-w-[103px] h-full"
          />
          <CustomButton
            type="secondary"
            label={t("add_to_preset", { defaultValue: "Add to preset" })}
            onClick={() => setIsExportPresetOpen(true)}
            disabled={!vehicleId}
            className="min-w-[121px] h-full"
          />
        </div>
      </div>

      {routeSettingQuery.isError && (
        <Alert className="mb-5" type="error" message={t("route_setting_load_failed")} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          className="w-full"
        >
          <div className="flex flex-wrap gap-x-4 gap-y-1 [&>.ant-form-item]:w-full md:[&>.ant-form-item]:w-[calc(50%_-_8px)]">
            <Form.Item
              name="routeMode"
              label={t("routeMode")}
              className={cn("order-1", {
                "[&_.ant-form-item-label]:!pb-0": !hasIgnitionStartStopEvents,
                "!mt-5": !showCriteriaFields,
              })}
            >
              {hasIgnitionStartStopEvents ? (
                <CustomSelect
                  options={routeModeOptions}
                  placeholder={t("select")}
                  className="common-select"
                />
              ) : (
                <div className="flex h-[32px] items-center text-sm font-semibold text-black">
                  {routeModeLabel}
                </div>
              )}
            </Form.Item>

            {showCriteriaFields && (
              <>
                <Form.Item
                  name="startStopCriteria"
                  label={t("startStopCriteria")}
                  className={cn("order-3 !mt-5", {
                    "[&_.ant-form-item-label]:!pb-0": !hasIgnitionStartStopEvents,
                  })}
                >
                  {hasIgnitionStartStopEvents ? (
                    <CustomSelect
                      options={startStopCriteriaOptions}
                      placeholder={t("select")}
                      className="common-select"
                    />
                  ) : (
                    <div className="flex h-[32px] items-center text-sm font-semibold text-black">
                      {t("onlyByCriteria")}
                    </div>
                  )}
                </Form.Item>

                <Form.Item
                  name="detectionBasedOn"
                  label={t("detectionBasedOn")}
                  className="order-2"
                  rules={[
                    {
                      required: true,
                      message: t("detection_based_on_is_required"),
                    },
                  ]}
                >
                  <CustomSelect
                    options={detectionBasedOnOptions}
                    placeholder={t("select")}
                    className="common-select"
                  />
                </Form.Item>

                {detectionBasedOn === "SPEED" && (
                  <Form.Item
                    name="minimumSpeedToStartARoute"
                    label={t("minimumSpeedToStartRoute")}
                    className="order-5"
                  >
                    <RouteSettingNumberInput className="common-input" />
                  </Form.Item>
                )}

                {detectionBasedOn === "GPS_COORDS" && (
                  <Form.Item name="geofenceInM" label={t("geofenceInM")} className="order-5">
                    <RouteSettingNumberInput className="common-input" />
                  </Form.Item>
                )}

                <Form.Item
                  name="validationPoint"
                  label={t("validationPointToStartRoute")}
                  className="order-6"
                >
                  <RouteSettingNumberInput className="common-input" />
                </Form.Item>

                <Form.Item
                  name="timeOut"
                  label={
                    <div>
                      <div>{t("timeOutInIdleStateInSec")}</div>
                      <div className="mt-1 text-xs font-normal text-gray-500">
                        {t("timeOutInIdleStateInSecSubtitle")}
                      </div>
                    </div>
                  }
                  className="order-7"
                >
                  <RouteSettingNumberInput className="common-input" />
                </Form.Item>

                <Form.Item
                  name="timeOutWithoutData"
                  label={
                    <div>
                      <div>{t("timeOutWithoutDataInSec")}</div>
                      <div className="mt-1 text-xs font-normal text-gray-500">
                        {t("timeOutWithoutDataInSecSubtitle")}
                      </div>
                    </div>
                  }
                  className="order-8"
                >
                  <RouteSettingNumberInput className="common-input" />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="maxSpeedKmh"
              label={
                <div>
                  <div>{t("maxSpeed")}</div>
                  <div className="mt-1 text-xs font-normal text-gray-500">
                    {t("speedHigherPointsIgnored")}
                  </div>
                </div>
              }
              className="order-4"
            >
              <CustomSelect
                options={maxSpeedOptions}
                placeholder={t("select")}
                className="common-select"
              />
            </Form.Item>

            <Form.Item
              name="timeoutNoData"
              label={
                <div>
                  <div>{t("timeOutToNoDataState")}</div>
                  <div className="mt-1 text-xs font-normal text-gray-500">
                    {t("timeOutToNoDataStateSubtitle")}
                  </div>
                </div>
              }
              className="order-9"
            >
              <RouteSettingNumberInput className="common-input" />
            </Form.Item>
          </div>

          <CustomActionButtons
            okProps={{
              htmlType: "submit",
              className: "!h-full !px-10.5 !text-[14px]",
            }}
            cancelProps={{
              className: "!h-full !px-9 !text-[14px]",
            }}
            onCancel={resetForm}
            loading={isSaving}
            disabled={!vehicleId}
            className="h-[32px]"
          />
        </Form>
      </Spin>

      <RouteSettingExportPresetModal
        open={isExportPresetOpen}
        routeSetting={routeSetting}
        onClose={() => setIsExportPresetOpen(false)}
        onSuccess={() => setIsPresetCreateSuccessOpen(true)}
      />

      <RouteSettingUsePresetModal
        open={isUsePresetOpen}
        vehicleId={vehicleId}
        onClose={() => setIsUsePresetOpen(false)}
        onSuccess={async (messageText) => {
          await routeSettingQuery.refetch();
          messageApi.success(messageText);
        }}
        onError={(messageText) => messageApi.error(messageText)}
      />

      <RouteSettingSaveSuccessModal
        open={isSaveSuccessOpen}
        onClose={() => setIsSaveSuccessOpen(false)}
      />

      <RoutePresetCreateSuccessModal
        open={isPresetCreateSuccessOpen}
        onClose={() => setIsPresetCreateSuccessOpen(false)}
      />
    </div>
  );
};

const DEFAULT_PRESET_PAGINATION = {
  page: 0,
  size: 10,
};

const getRoutePresetRecords = (data?: {
  data?: {
    response?: {
      presets?: RoutePresetRecord[];
      resources?: RoutePresetRecord[];
    };
  };
}) => data?.data?.response?.presets ?? data?.data?.response?.resources ?? [];

const getRoutePresetTotal = (
  data:
    | {
        data?: {
          response?: {
            totalItems?: number;
            totalElements?: number;
            total?: number;
          };
        };
      }
    | undefined,
  fallback: number
) => {
  const response = data?.data?.response;
  return Number(response?.totalItems ?? response?.totalElements ?? response?.total ?? fallback);
};

const formatRoutePresetEnum = (value?: string | null) => {
  if (!value) return "--";

  const labels: Record<string, string> = {
    MANAGED_BY_IGNITION: "Managed by ignition",
    MANAGED_BY_SERVER: "Managed by server",
    IGNITION: "Ignition",
    ONLY_CRITERIA: "Only by criteria",
    SPEED: "Speed",
    GPS_COORDS: "GPS coords",
  };

  return labels[value] ?? value.replace(/_/g, " ");
};

const formatRoutePresetValue = (value: unknown, suffix = "") => {
  if (value === null || value === undefined || value === "") return "--";
  return `${typeof value === "number" || typeof value === "string" ? formatNumber(value, i18n.language) : "--"}${suffix}`;
};

const getRoutePresetCriteriaLabel = (record?: RoutePresetRecord | null) => {
  if (!record) return "--";
  if (record.routeMode === "MANAGED_BY_IGNITION")
    return formatRoutePresetEnum(record.startStopCriteria);
  return "Only by criteria";
};

const RoutePresetDetailRow = ({ label, value }: { label: string; value: unknown }) => (
  <div className="flex min-h-[48px] items-center gap-x-4 border-b border-gray2 py-[10px] last:border-b-0 max-[420px]:flex-wrap max-[420px]:gap-y-1">
    <span className="shrink-0 text-[14px] leading-[18px] font-semibold text-black">{label}</span>
    <span className="min-w-0 text-[14px] leading-[18px] font-normal text-black">
      {value === null || value === undefined || value === "" ? "--" : String(value)}
    </span>
  </div>
);

const RoutePresetDetailsModal = ({
  open,
  record,
  onClose,
  onDelete,
}: {
  open: boolean;
  record?: RoutePresetRecord | null;
  onClose: () => void;
  onDelete: (record: RoutePresetRecord) => void;
}) => {
  const { t } = useTranslation();
  const showSpeed = record?.detectionBasedOn === "SPEED";

  return (
    <CustomModal
      open={open}
      onCancel={onClose}
      showFooter={false}
      width={398}
      styles={{
        content: {
          height: 498,
          maxHeight: "calc(100vh - 32px)",
          padding: "28px",
        },
        body: { height: "100%" },
      }}
      destroyOnHidden
      centered
    >
      <div className="flex h-full min-h-0 flex-col">
        <CustomTypography.CustomText className="!block !pr-8 !text-[17.72px] !leading-[28px] !font-bold !text-black">
          {record?.name || t("preset_name", { defaultValue: "Preset name" })}
        </CustomTypography.CustomText>

        <div className="mt-5 min-h-0 flex-1 overflow-y-auto pr-1">
          <RoutePresetDetailRow
            label={`${t("preset_detail_route_mode")}:`}
            value={formatRoutePresetEnum(record?.routeMode)}
          />
          <RoutePresetDetailRow
            label={`${t("preset_detail_start_stop_criteria")}:`}
            value={getRoutePresetCriteriaLabel(record)}
          />
          <RoutePresetDetailRow
            label={`${t("preset_detail_detection_based_on")}:`}
            value={formatRoutePresetEnum(record?.detectionBasedOn)}
          />
          <RoutePresetDetailRow
            label={`${
              showSpeed ? t("preset_detail_minimum_speed") : t("preset_detail_geofence_in_m")
            }:`}
            value={formatRoutePresetValue(showSpeed ? record?.minSpeedKmh : record?.geofenceMeters)}
          />
          <RoutePresetDetailRow
            label={`${t("preset_detail_validation_point")}:`}
            value={formatRoutePresetValue(record?.validationPoints)}
          />
          <RoutePresetDetailRow
            label={`${t("preset_detail_idle_timeout")}:`}
            value={formatRoutePresetValue(record?.idleTimeoutSec)}
          />
          <RoutePresetDetailRow
            label={`${t("preset_detail_without_data_timeout")}:`}
            value={formatRoutePresetValue(record?.noDataTimeoutSec)}
          />
        </div>

        <div className="mt-5 flex shrink-0 justify-end gap-[10px]">
          <CustomTooltip title={record?.isBase ? t("base_presets_cannot_be_deleted") : ""}>
            <span>
              <CustomButton
                type="secondary"
                danger
                label={t("delete")}
                disabled={!record || record.isBase}
                onClick={() => record && onDelete(record)}
                className="!h-[32px] min-w-[120px] !text-[14px] !leading-[18px] !font-semibold"
              />
            </span>
          </CustomTooltip>
          <CustomButton
            type="secondary"
            label={t("close")}
            onClick={onClose}
            className="!h-[32px] min-w-[120px] !text-[14px] !leading-[18px] !font-semibold"
          />
        </div>
      </div>
    </CustomModal>
  );
};

const RoutePresetCloneModal = ({
  record,
  onClose,
  onSuccess,
  onError,
}: {
  record?: RoutePresetRecord | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ name: string }>();
  const presetNamesQuery = useQueryGetRoutePresets({
    enabled: Boolean(record),
  });
  const cloneMutation = useMutationCloneRoutePreset();
  const nameValue = Form.useWatch("name", form) ?? "";
  const existingNames = useMemo(
    () => getRoutePresetNames(presetNamesQuery.data),
    [presetNamesQuery.data]
  );
  const isDuplicate =
    Boolean(nameValue.trim()) && existingNames.includes(nameValue.trim().toLowerCase());

  useEffect(() => {
    if (record) form.resetFields();
  }, [form, record]);

  const handleClone = async () => {
    if (!record) return;
    const values = await form.validateFields();
    const name = values.name.trim();

    if (existingNames.includes(name.toLowerCase())) {
      form.setFields([{ name: "name", errors: [t("preset_name_already_exists")] }]);
      return;
    }

    try {
      await cloneMutation.mutateAsync({ id: record.id, name });
      form.resetFields();
      onClose();
      onSuccess();
    } catch {
      onError();
    }
  };

  return (
    <CustomModal
      open={Boolean(record)}
      onCancel={onClose}
      showFooter={false}
      width={473}
      closable={false}
      styles={{ content: { minHeight: 230 } }}
      destroyOnHidden
      centered
    >
      <CustomTypography.CustomText className="!block !text-center !text-[19.93px] !leading-[28px] !font-bold !text-black">
        {t("clone_preset", { defaultValue: "Clone preset" })}
      </CustomTypography.CustomText>

      <Form form={form} layout="vertical" requiredMark={false} className="!mt-7">
        <Form.Item
          name="name"
          label={t("preset_name")}
          className="!mb-0 [&_.ant-form-item-label]:!pb-0 [&_.ant-form-item-label>label]:!text-[14px] [&_.ant-form-item-label>label]:!leading-[18px] [&_.ant-form-item-label>label]:!font-normal [&_.ant-form-item-label>label]:!text-gray9"
          validateStatus={isDuplicate ? "error" : undefined}
          help={isDuplicate ? t("preset_name_already_exists") : undefined}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("preset_name_is_required"),
            },
          ]}
        >
          <CustomInput
            placeholder={t("type_preset_name")}
            className="common-input !mt-2 !h-[32px] !py-0"
          />
        </Form.Item>
      </Form>

      <CustomActionButtons
        okBtnLabel={t("save")}
        cancelBtnLabel={t("cancel")}
        loading={cloneMutation.isPending}
        disabled={isDuplicate || !record}
        onOk={handleClone}
        onCancel={onClose}
        className="mt-8 !h-[32px]"
        cancelProps={{
          className: "!h-full flex-1 !text-[14px] !leading-[18px] !font-semibold !text-primary",
        }}
        okProps={{
          className: "!h-full flex-1 !text-[14px] !leading-[18px] !font-semibold !text-gray2",
        }}
      />
    </CustomModal>
  );
};

const RoutePresetsTable = () => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [pagination, setPagination] = useState(DEFAULT_PRESET_PAGINATION);
  const [sortConfig, setSortConfig] = useState<{
    sort: RoutePresetSortDirection;
    sortBy: string;
  }>({
    sort: "DESC",
    sortBy: "isBase",
  });
  const [deleteRecord, setDeleteRecord] = useState<RoutePresetRecord | null>(null);
  const [detailRecord, setDetailRecord] = useState<RoutePresetRecord | null>(null);
  const [cloneRecord, setCloneRecord] = useState<RoutePresetRecord | null>(null);

  const presetsQuery = useQueryGetRoutePresetsPagedList({
    page: pagination.page,
    size: pagination.size,
    sort: sortConfig.sort,
    sortBy: sortConfig.sortBy,
  });
  const deleteMutation = useMutationDeleteRoutePreset();

  const records = useMemo(() => getRoutePresetRecords(presetsQuery.data), [presetsQuery.data]);
  const totalCount = getRoutePresetTotal(presetsQuery.data, records.length);

  const handleTableChange: TableProps<RoutePresetRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!currentSorter?.order) {
      setSortConfig({
        sort: "DESC",
        sortBy: "isBase",
      });
      setPagination((previous) => ({ ...previous, page: 0 }));
      return;
    }

    const columnKey = String(currentSorter.columnKey ?? currentSorter.field ?? "");
    const sortByMap: Record<string, string> = {
      name: "name",
      isBase: "isBase",
      routeMode: "routeMode",
      detectionBasedOn: "detectionBasedOn",
    };

    setSortConfig({
      sort: currentSorter.order === "ascend" ? "ASC" : "DESC",
      sortBy: sortByMap[columnKey] ?? "isBase",
    });
    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;

    try {
      await deleteMutation.mutateAsync(deleteRecord.id);
      messageApi.success(t("preset_deleted_successfully"));
      setDeleteRecord(null);
    } catch {
      messageApi.error(t("error"));
    }
  };

  const columns: TableProps<RoutePresetRecord>["columns"] = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (value) => (
        <CustomTypography.CustomText className="!text-sm !font-[600] !leading-[18px] !text-black">
          {value || "--"}
        </CustomTypography.CustomText>
      ),
    },
    {
      title: t("preset_type", { defaultValue: "Preset type" }),
      dataIndex: "isBase",
      key: "isBase",
      sorter: true,
      render: (isBase) =>
        isBase ? (
          <CustomTag className="bg-green4 text-green2">
            {t("base_preset", { defaultValue: "Base preset" })}
          </CustomTag>
        ) : (
          <CustomTag className="bg-[#F1F3F9] text-[#7D88B1]">
            {t("created_preset", { defaultValue: "Created preset" })}
          </CustomTag>
        ),
    },
    {
      title: t("routeMode"),
      dataIndex: "routeMode",
      key: "routeMode",
      sorter: true,
      render: (value) => (
        <CustomTypography.CustomText className="!text-[14px] !leading-[18px] !font-semibold !text-black">
          {formatRoutePresetEnum(value)}
        </CustomTypography.CustomText>
      ),
    },
    {
      title: t("detectionBasedOn"),
      dataIndex: "detectionBasedOn",
      key: "detectionBasedOn",
      sorter: true,
      render: (value) => (
        <CustomTypography.CustomText className="!text-[14px] !leading-[18px] !font-normal !text-black">
          {formatRoutePresetEnum(value)}
        </CustomTypography.CustomText>
      ),
    },
    {
      title: t("action"),
      key: "actions",
      align: "center",
      width: 110,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-1">
          <CustomTooltip title={t("clone", { defaultValue: "Clone" })}>
            <button
              type="button"
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-[4px] transition-colors hover:bg-gray1"
              onClick={(event) => {
                event.stopPropagation();
                setCloneRecord(record);
              }}
            >
              <CloneIconComponent className="h-[16px] w-[16px]" />
            </button>
          </CustomTooltip>
          <CustomTooltip title={record.isBase ? t("base_presets_cannot_be_deleted") : t("delete")}>
            <button
              type="button"
              disabled={record.isBase}
              className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-[4px] transition-colors cursor-pointer hover:bg-gray1",
                record.isBase && "cursor-not-allowed opacity-30 grayscale hover:bg-transparent"
              )}
              onClick={(event) => {
                event.stopPropagation();
                if (!record.isBase) setDeleteRecord(record);
              }}
            >
              <DeleteIconComponent className="h-[16px] w-[15px]" />
            </button>
          </CustomTooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="p-5">
      {contextHolder}
      <CustomTypography.CustomText className="!mb-5 !block !text-[17.72px] !leading-[28px] !font-bold !text-black">
        {t("preset", { defaultValue: "Preset" })}
      </CustomTypography.CustomText>

      <CustomTable<RoutePresetRecord>
        customClass="customers-table route-presets-table"
        data={records}
        columns={columns}
        rowKey={(record) => String(record.id)}
        isLoading={presetsQuery.isLoading || presetsQuery.isFetching}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={totalCount}
        handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
        handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
        onChange={handleTableChange}
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        isCustomRowClick
        customRowClick={setDetailRecord}
        locale={{ emptyText: t("no_data") }}
      />

      <CustomSharedConfirmationModal
        open={Boolean(deleteRecord)}
        title={t("delete_preset")}
        description={t("delete_preset_confirmation")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDelete}
        centered
      />

      <RoutePresetDetailsModal
        open={Boolean(detailRecord)}
        record={detailRecord}
        onClose={() => setDetailRecord(null)}
        onDelete={(record) => {
          setDetailRecord(null);
          setDeleteRecord(record);
        }}
      />

      <RoutePresetCloneModal
        record={cloneRecord}
        onClose={() => setCloneRecord(null)}
        onSuccess={() => messageApi.success(t("route_preset_cloned_successfully"))}
        onError={() => messageApi.error(t("error"))}
      />
    </div>
  );
};

const DEFAULT_TIME_RANGE: [Dayjs, Dayjs] = [
  dayjs().hour(0).minute(0).second(0),
  dayjs().hour(23).minute(59).second(59),
];

const RoutesHistoryTab = ({ vehicleId, unitId }: RoutesHistoryTabProps) => {
  const { t } = useTranslation();
  const [activeRouteSection, setActiveRouteSection] = useState("history");
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [timeRange, setTimeRange] = useState<[Dayjs, Dayjs]>(DEFAULT_TIME_RANGE);
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const routeListRef = useRef<VirtuosoHandle>(null);

  const formatEpoch = useEpochFormatter();

  const startHour = timeRange[0].hour();

  const endHour =
    timeRange[1].hour() === 23 && timeRange[1].minute() === 59 ? 24 : timeRange[1].hour();

  const { data, isLoading, isFetching, isError } = useQueryGetRouteHistory(
    vehicleId
      ? {
          vehicleId,
          date: date.format("YYYY-MM-DD"),
          startHour,
          endHour,
        }
      : undefined,
    { enabled: !!vehicleId }
  );

  const routePoints = useMemo(
    () => normalizeRoutePoints(data?.data.response ?? []),
    [data?.data.response]
  );

  const selectedPoint = routePoints.find((point) => point.id === selectedPointId);

  useEffect(() => {
    if (!selectedPointId) return;

    const selectedIndex = routePoints.findIndex((point) => point.id === selectedPointId);
    if (selectedIndex < 0) return;

    const animationFrame = window.requestAnimationFrame(() => {
      routeListRef.current?.scrollToIndex({
        index: selectedIndex,
        align: "center",
        behavior: "smooth",
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [routePoints, selectedPointId]);

  const handleSelectPoint = (point: NormalizedRoutePoint) => {
    setSelectedPointId((previous) => (previous === point.id ? null : point.id));
  };
  const handleClosePointPopup = (pointId: string) => {
    setSelectedPointId((previous) => (previous === pointId ? null : previous));
  };

  return (
    <div className="rounded-[16px] bg-white px-[18px] py-5">
      <div className="mb-8">
        <CustomTypography.CustomText className="!block !text-[19.93px] !leading-[28px] !font-bold !text-black">
          {t("route")}
        </CustomTypography.CustomText>
        <CustomTypography.CustomText className="!mt-2 !block !text-sm !leading-[22px] !text-gray9">
          {t("route_history_description")}
        </CustomTypography.CustomText>
      </div>

      <div className="flex flex-col gap-8 xl:flex-row">
        <aside className="flex shrink-0 flex-row gap-1.5 overflow-x-auto xl:w-max xl:flex-col xl:overflow-visible">
          {routeSections.map((section) => {
            const active =
              section.key === activeRouteSection ||
              ("children" in section &&
                section.children?.some((child) => child.key === activeRouteSection));

            return (
              <div key={section.key} className="flex w-full shrink-0 flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (section.key === "history") setActiveRouteSection("history");
                    if (section.key === "settings") setActiveRouteSection("configuration");
                  }}
                  className={cn(
                    "h-[38px] w-full shrink-0 cursor-pointer whitespace-nowrap rounded-none px-2 text-left text-[14px] font-semibold !leading-[18px] transition-colors",
                    active ? "bg-primary6 text-primary" : "bg-white text-gray9 hover:bg-gray1"
                  )}
                >
                  {t(section.labelKey)}
                </button>

                {"children" in section &&
                  section.children?.map((child) => {
                    const childActive = child.key === activeRouteSection;
                    const childDisabled = child.key !== "configuration" && child.key !== "preset";

                    return (
                      <button
                        key={child.key}
                        type="button"
                        disabled={childDisabled}
                        onClick={() => setActiveRouteSection(child.key)}
                        className={cn(
                          "-mt-1 h-[38px] w-full shrink-0 cursor-pointer whitespace-nowrap bg-white pl-8 pr-2 text-left text-[14px] font-semibold !leading-[18px]",
                          childActive
                            ? "text-primary"
                            : childDisabled
                              ? "cursor-not-allowed text-gray9"
                              : "text-gray9 hover:bg-gray1"
                        )}
                      >
                        {t(child.labelKey)}
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </aside>

        <div className="min-w-0 flex-1 overflow-hidden rounded-[16px] border border-[#d7dceb] bg-white">
          {activeRouteSection === "configuration" ? (
            <RouteSettingConfiguration vehicleId={vehicleId} unitId={unitId} />
          ) : activeRouteSection === "preset" ? (
            <RoutePresetsTable />
          ) : (
            <div className="grid min-h-[600px] grid-cols-1 lg:grid-cols-[minmax(420px,0.9fr)_minmax(520px,1.1fr)]">
              <section className="pl-4 py-4">
                <CustomTypography.CustomText className="!block !text-[17.72px] !leading-[28px] !font-bold !text-black mb-4">
                  {t("route_history")}
                </CustomTypography.CustomText>

                <div className="mb-5 flex w-full items-end gap-3">
                  <div className="min-w-0 flex-[1_1_48%]">
                    <div className="mb-2 text-sm leading-[18px] text-gray9">{t("selectDate")}</div>
                    <CustomSharedDatePicker
                      value={date}
                      allowClear={false}
                      suffixIcon={<CalendarOutlined className="text-gray8" />}
                      className="!h-[32px] !w-full !rounded-[4px]"
                      onChange={(value: Dayjs | null) => {
                        if (value) setDate(value);
                        setSelectedPointId(null);
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <CustomButton
                      type="secondary"
                      buttonSize="small"
                      className="!h-[32px] !w-[32px] rounded-[4px] border border-gray5"
                      aria-label={t("previous")}
                      icon={
                        <svg
                          width="7"
                          height="11"
                          viewBox="0 0 7 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.164731 5.41385L6.20223 10.1295C6.21801 10.1419 6.23697 10.1496 6.25693 10.1517C6.2769 10.1539 6.29706 10.1503 6.3151 10.1415C6.33314 10.1327 6.34833 10.119 6.35893 10.1019C6.36952 10.0849 6.37509 10.0652 6.375 10.0451L6.375 9.00983C6.375 8.9442 6.3442 8.88126 6.2933 8.84108L1.47187 5.07635L6.2933 1.31161C6.34554 1.27144 6.375 1.20849 6.375 1.14286L6.375 0.107596C6.375 0.0178642 6.27187 -0.0316897 6.20223 0.023221L0.164731 4.73885C0.113416 4.77887 0.0719046 4.83008 0.0433526 4.88856C0.0148005 4.94704 -4.00817e-05 5.01127 -4.00874e-05 5.07635C-4.00931e-05 5.14143 0.0148005 5.20565 0.0433526 5.26413C0.0719046 5.32262 0.113416 5.37382 0.164731 5.41385Z"
                            fill="#020304"
                          />
                        </svg>
                      }
                      disabled={isFetching}
                      onClick={() => {
                        setDate((previous) => previous.subtract(1, "day"));
                        setSelectedPointId(null);
                      }}
                    />

                    <CustomButton
                      type="secondary"
                      buttonSize="small"
                      className="!h-[32px] !w-[32px] rounded-[4px] border border-gray5"
                      aria-label={t("next")}
                      icon={
                        <svg
                          width="7"
                          height="11"
                          viewBox="0 0 7 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.21027 4.7385L0.172769 0.0228723C0.156993 0.0104521 0.138033 0.00273343 0.118068 0.000602754C0.0981026 -0.00152792 0.0779412 0.0020158 0.0598992 0.010827C0.0418571 0.0196383 0.0266664 0.0333596 0.0160712 0.0504152C0.00547591 0.0674708 -9.40343e-05 0.087169 1.20111e-06 0.107247V1.14252C1.20111e-06 1.20814 0.0308049 1.27109 0.0816977 1.31127L4.90313 5.076L0.0816977 8.84073C0.0294656 8.88091 1.20111e-06 8.94386 1.20111e-06 9.00948V10.0447C1.20111e-06 10.1345 0.103126 10.184 0.172769 10.1291L6.21027 5.4135C6.26158 5.37347 6.3031 5.32227 6.33165 5.26378C6.3602 5.2053 6.37504 5.14108 6.37504 5.076C6.37504 5.01092 6.3602 4.94669 6.33165 4.88821C6.3031 4.82973 6.26158 4.77853 6.21027 4.7385Z"
                            fill="#020304"
                          />
                        </svg>
                      }
                      disabled={isFetching}
                      onClick={() => {
                        setDate((previous) => previous.add(1, "day"));
                        setSelectedPointId(null);
                      }}
                    />
                  </div>

                  <div className="min-w-0 flex-[1_1_36%]">
                    <div className="mb-2 text-sm font-medium text-gray9">{t("select_time")}</div>
                    <CustomTimeRangePicker
                      value={timeRange}
                      allowClear={false}
                      format="HH:00"
                      className="!h-[32px] !w-full"
                      onChange={(range) => {
                        if (!range) return;

                        setTimeRange(range as [Dayjs, Dayjs]);
                        setSelectedPointId(null);
                      }}
                    />
                  </div>
                </div>

                {isError && (
                  <div className="pt-4">
                    <Alert type="error" message={t("routeHistoryLoadFailed")} showIcon />
                  </div>
                )}

                <Spin spinning={isLoading || isFetching}>
                  <div className="mt-8">
                    {!routePoints.length && !isLoading ? (
                      <div className="flex h-[320px] items-center justify-center px-4">
                        <CustomEmpty description={t("noRoutesFound")} />
                      </div>
                    ) : (
                      <CustomVirtualScroll<NormalizedRoutePoint>
                        ref={routeListRef}
                        items={routePoints}
                        height={452}
                        width="100%"
                        className="[&::-webkit-scrollbar]:!w-[4px] [&::-webkit-scrollbar-thumb]:!bg-gray5 [&::-webkit-scrollbar-track]:!bg-[#D9DCE7]"
                        style={{ direction: "rtl" }}
                        renderItem={(_index, point) => {
                          const selected = selectedPoint?.id === point.id;
                          const showStart = point.routeStart && !point.routeEnd;
                          const showEnd = point.routeEnd && !point.routeStart;

                          return (
                            <div className="pb-2 pl-2 pr-1 [direction:ltr]">
                              <button
                                type="button"
                                className={cn(
                                  "flex min-h-[56px] w-full cursor-pointer items-center rounded-[8px] border bg-white px-4 py-3 text-left transition-colors",
                                  selected
                                    ? "border-primary bg-primary6"
                                    : "border-gray3 hover:bg-gray1 hover:border-gray1"
                                )}
                                onClick={() => handleSelectPoint(point)}
                              >
                                <span className="flex min-w-0 flex-1 items-center gap-4">
                                  <span className="flex items-center gap-2 leading-[18px] font-[600] text-black">
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <g clip-path="url(#clip0_1136_16682)">
                                        <path
                                          d="M8 0C3.58769 0 0 3.58769 0 8C0 12.4123 3.58769 16 8 16C12.4123 16 16 12.4123 16 8C16 3.58769 12.4123 0 8 0ZM8 14.7692C4.26462 14.7692 1.23077 11.7354 1.23077 8C1.23077 4.26462 4.26462 1.23077 8 1.23077C11.7354 1.23077 14.7692 4.26462 14.7692 8C14.7692 11.7354 11.7354 14.7692 8 14.7692Z"
                                          fill="#666E8D"
                                        />
                                        <path
                                          d="M7.38473 7.38425H4.31396V8.61502H8.6155V4.31348H7.38473V7.38425Z"
                                          fill="#666E8D"
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_1136_16682">
                                          <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                    {formatRouteTime(point.timestamp)}
                                  </span>

                                  <span className="flex items-center gap-2 leading-[18px] text-gray9">
                                    <svg
                                      width="17"
                                      height="16"
                                      viewBox="0 0 17 16"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <g clip-path="url(#clip0_1136_16687)">
                                        <path
                                          d="M13.0769 3.2064V0H11.7692V3.2064H5.23731V0H3.92962V3.2064H0V16H17V3.2064H13.0769ZM15.6923 4.4864V7.0016H1.30769V4.4864H15.6923ZM1.30769 14.72V8.2816H15.6923V14.72H1.30769Z"
                                          fill="#666E8D"
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_1136_16687">
                                          <rect width="17" height="16" fill="white" />
                                        </clipPath>
                                      </defs>
                                    </svg>

                                    {formatEpoch(point.timestamp)}
                                  </span>
                                </span>

                                {!showStart && !showEnd && (
                                  <span className="mx-3 text-gray8">|</span>
                                )}

                                {showStart && (
                                  <CustomTag className="mr-4 flex bg-green3 uppercase text-green2">
                                    {t("starting_point")}
                                  </CustomTag>
                                )}

                                {showEnd && (
                                  <CustomTag className="mr-4 flex bg-red-50 uppercase text-red-500">
                                    {t("ending_point")}
                                  </CustomTag>
                                )}

                                <span className="flex items-center gap-2 text-[15.75px] font-[600] leading-[20px] text-black">
                                  <svg
                                    width="17"
                                    height="16"
                                    viewBox="0 0 17 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g clip-path="url(#clip0_1711_14555)">
                                      <path
                                        d="M8.7905 16C6.60899 16 4.55665 15.1748 3.00663 13.6783L4.01845 12.6853C5.29578 13.9231 6.98932 14.6014 8.7905 14.6014C12.522 14.6014 15.5647 11.6434 15.5647 8C15.5647 4.35664 12.522 1.3986 8.7905 1.3986C6.96062 1.3986 5.25272 2.0979 3.96821 3.36364L2.94922 2.38462C4.49924 0.846154 6.57311 0 8.7905 0C13.3186 0 16.9999 3.58741 16.9999 8C16.9999 12.4126 13.3186 16 8.7905 16Z"
                                        fill="#7F89AF"
                                      />
                                      <path
                                        d="M10.2337 4.13055L8.1709 7.65332L9.41744 8.34648L11.4802 4.82371L10.2337 4.13055Z"
                                        fill="#7F89AF"
                                      />
                                      <path
                                        d="M4.49937 4.34277H0V6.09103H4.49937V4.34277Z"
                                        fill="#7F89AF"
                                      />
                                      <path
                                        d="M4.49937 9.90918H0V11.6574H4.49937V9.90918Z"
                                        fill="#7F89AF"
                                      />
                                      <path
                                        d="M6.16423 7.12598H2.15283V8.87423H6.16423V7.12598Z"
                                        fill="#7F89AF"
                                      />
                                      <path
                                        d="M8.79052 9.24439C9.49597 9.24439 10.0678 8.6871 10.0678 7.99964C10.0678 7.31218 9.49597 6.75488 8.79052 6.75488C8.08506 6.75488 7.51318 7.31218 7.51318 7.99964C7.51318 8.6871 8.08506 9.24439 8.79052 9.24439Z"
                                        fill="#7F89AF"
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_1711_14555">
                                        <rect width="17" height="16" fill="white" />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                  {formatSpeed(point.speed)}
                                </span>
                              </button>
                            </div>
                          );
                        }}
                      />
                    )}
                  </div>
                </Spin>
              </section>

              <section className="p-5">
                <RouteHistoryMap
                  points={routePoints}
                  selectedPoint={selectedPoint}
                  onSelectPoint={handleSelectPoint}
                  onClosePopup={handleClosePointPopup}
                />
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutesHistoryTab;
